import React, {Fragment, useState} from 'react';
import {observer} from "mobx-react";
import HeightPin from "../HeightPin";
import ScrollSection from "../ScrollSection";
import GemDiscover from "../elements/GemDiscover";
import MoodBoard from "../MoodBoard"
import ImageCirclePack from "../charts/ImageCirclePack";
import TreeMap from "../charts/TreeMap";
import CardFlip from "../elements/CardFlip";
import DidYouKnow from "../elements/DidYouKnow";
import FormElement from "../elements/FormElement";
import Results from "../elements/Results";
import SectionTop from "./SectionTop";
import {getAltText, getSectionImage, getInfoClass, getImageInfoStyle} from "../../assets/settings";

function GettingAround(props) {
    const {store, state, color, index, downhill} = props;
    const {circlePackStore} = store;

    const group = 'GA';
    const activeKey = store.activeSection(group) || 'GA_2';


    const sectionY = 0.35;

    const backgroundStyle = {
      backgroundImage: `url(img/GA/GA_background.jpg)`
    };

    return (
        <div className={'Section'}>
            <SectionTop group={group} downhill={downhill}>
                  <h4>
                    Campus mobility refers to how employees, students, and visitors get to and around campus.
                  </h4>
                  <p>
                    In order to fully participate in academic and campus life, people need to be able to get to campus, and to get around once they are there. People should be able to get to campus in ways that are convenient, affordable, environmentally-friendly, and safe.
                  </p>
                  <p>
                    Creating a clear and intuitive network of walking, bicycling, transit, and driving routes can help the campus feel more welcoming, cohesive, and easy to get around.
                  </p>
                  <p className="photocredit">
                    Photo Credit: UC Berkeley
                  </p>
            </SectionTop>
            <HeightPin store={store} state={state} scrollChild={<div className={'left-scroll'}>
                <div className={'spacer'} style={{height: 50}}/>

                <ScrollSection store={store} sectionY={sectionY} background={'none'} sectionGroup={group}
                               sectionKey={`GA_2`}>
                    <div style={getImageInfoStyle(color)} className={getInfoClass(activeKey, 'GA_2')}>
                      <h4>How do people get to campus?</h4>
                      <p>
                        Students tend to walk, bike or take transit to campus -- 91% use these modes! Faculty and staff are more likely to drive, but less than half of them get to campus in a car.
                      </p>
                      <p>
                        Although the percentage of people who drive to campus has steadily decreased over the past twenty years, parking continues to be of concern. Parking lots and garages are largely at capacity day-to-day.
                      </p>
                      <p>
                        The percentage of trips to campus on a bicycle has increased by almost 50% since 2005. The rate of “drive alone” trips has dropped from 24% in 2002 to 15% in 2019.
                      </p>
                    </div>
                </ScrollSection>
                <ScrollSection store={store} sectionY={sectionY} background={'none'} sectionGroup={group}
                               sectionKey={`GA_3`}>
                    <div style={getImageInfoStyle(color)} className={getInfoClass(activeKey, 'GA_3')}>
                        <h4>
                          Where do people take AC Transit to get to campus?
                        </h4>
                        <p>
                          There are over 11,000 average weekday boardings and alightings on AC Transit, for routes with stops on the Campus Park perimeter. AC Transit ridership is highest in the southern area of campus.
                        </p>
                        <p>
                          Line 51B (Rockridge to Berkeley) has the highest ridership, with around 4,200 daily riders, followed by Line 52 (University Village to UC Berkeley), with around 2,700 daily riders.
                        </p>
                    </div>
                </ScrollSection>
                <ScrollSection store={store} sectionY={sectionY} background={'none'} sectionGroup={group}
                               sectionKey={`GA_4`}>
                    <div style={getImageInfoStyle(color)} className={getInfoClass(activeKey, 'GA_4')}>
                      <h4>
                        Where do people walk around campus?
                      </h4>
                      <p>
                        Based on multi-modal traffic counts between 7am and 7pm, Sproul Plaza has the highest concentration of pedestrians on campus.
                      </p>
                      <p>
                        The campus’s hilly topography can make it challenging to create accessible paths of travel. However, the campus has developed a network of preferred wheelchair routes to all buildings, and provides the LOOP shuttle to help overcome these topographical issues.
                      </p>
                    </div>
                </ScrollSection>
                <ScrollSection store={store} sectionY={sectionY} background={'none'} sectionGroup={group}
                               sectionKey={`GA_5`}>
                    <div style={getImageInfoStyle(color)} className={getInfoClass(activeKey, 'GA_5')}>
                      <h4>
                        Where do people bike around campus?
                      </h4>
                      <p>
                        Bicycle activity is highest on the north, west, and south sides of campus. Because it is a continuous and relatively flat north-south route through campus, Sproul Plaza has a significant volume of bicycles, but also high potential for conflicts between pedestrians and bicycles.
                      </p>
                      <p>
                        BayWheels, the regional bike share program, has around 200 riders per day at the five stations around campus.
                      </p>
                      <p>
                        In the 2019 LRDP & Campus Master Plan survey, respondents cited a need to improve pathway conditions to make it easier to bike around campus.
                      </p>
                    </div>
                </ScrollSection>
                <ScrollSection store={store} sectionY={sectionY} background={'none'} sectionGroup={group}
                               sectionKey={`GA_6`}>
                    <div style={getImageInfoStyle(color)} className={getInfoClass(activeKey, 'GA_6')}>
                      <h4>
                        Where do people drive around campus?
                      </h4>
                      <p>
                        Vehicles entering and exiting the Campus Park are associated with parking, drop-offs, deliveries, service operations, and Uber/Lyft services.
                      </p>
                      <p>
                        The University Drive & Gayley Road (East Gate) entrance is the most heavily used vehicular gateway, followed by the West Circle.
                      </p>
                      <DidYouKnow store={store} color={color}>
                          <p>
                            The Campus Park has about 600 parking spaces--that's enough asphalt to cover about 3.5 football fields!
                          </p>
                      </DidYouKnow>
                    </div>
                </ScrollSection>

                <div className={'spacer'} style={{height: '40vh'}}/>
            </div>}
                       pinChild={<div className={'sticky-image-right'}>
                           <div className={'frame'}>
                              {getAltText(activeKey).title && <h4 style={{'backgroundColor': color + '80'}}>{getAltText(activeKey).title}</h4>}
                               <img width={'100%'} src={getSectionImage(group, activeKey)}  alt={getAltText(activeKey).alt}/>
                           </div>
                       </div>}
            />
            <div className={'interactive'}>
                <CardFlip color={color} index={index} store={store}
                        frontFace={<FormElement store={store} id={group} />}
                        backFace={<Fragment>
                            <Results store={store} id={group} />
                            {/*<GemDiscover store={store} color={color} index={index}/>*/}

                        </Fragment>}/>
            </div>
        </div>
    );
}

export default observer(GettingAround);
